import React from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import _ from 'lodash';
import { reduxForm, getValues } from 'redux-form';

import TrustPilotBadge from '@src/components/TrustPilotBadge/TrustPilotBadge';

import Person from './Person';
import { saveCustomerState } from '../../actions';
import CompanyDirectorNotice from '../../components/CompanyDirectorNotice';
import FinishStepButton from '../../components/FinishStepButton/FinishStepButton';
import { buildApplicationState } from '../../helpers/applicationManager';
import applicationSchemaDE from '../../helpers/applicationSchemaDE';
import { localeCurrency } from '../../helpers/constants';
import { scrollToErrors } from '../../helpers/scrollToErrors';
import {
    getCountryCode,
    hasAutomatedDecision
} from '../../lendingAPI/lendingAPISelector';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';
import { getLoanDecision as getLoanDecisionAction } from '../LoanDecision/LoanDecision.actions';
import { OPEN_MODAL } from '../Modals/Modals.actions';

class PersonalInformationFields extends React.Component {
    // eslint-disable-next-line react/no-deprecated
    componentWillReceiveProps(nextProps) {
        const {
            fields: { people }
        } = nextProps;
        const applicant = _.find(people, (person) =>
            _.includes(person.roles.value, 'applicant')
        );
        if (people.length && !applicant) {
            // eslint-disable-next-line no-undef
            let emptyPerson = _.first(applicationSchema.people);
            people.addField(emptyPerson);
        }
    }

    handleOnSubmit = () => {
        const applicant = _.find(this.props.fields.people, (person) => {
            return _.includes(person.roles.value, 'applicant');
        });
        const hasPreviouslyAgreedPrivacyPolicy =
            applicant.privacy_policy_agreement_date.value;
        if (!hasPreviouslyAgreedPrivacyPolicy) {
            const newState = buildApplicationState(
                this.props.application.payload,
                this.props.formValues
            );
            const stateToSave = {
                data: {
                    application: newState,
                    ui: this.props.ui
                }
            };
            this.props.saveCustomerState(stateToSave);
            return this.props.showCreditCheckOverlay();
        }

        tracking.ampli.personalDetailsSubmitted({
            ...getAmplitudeDefaultEventProperties()
        });

        this.props.getLoanDecision();
    };

    stateWithChangedFormValues = () => {
        const { formValues, application, ui } = this.props;
        const newState = buildApplicationState(application.payload, formValues);
        return { data: { application: newState, ui } };
    };

    getExplainerText = () => {
        const { hasAutomatedDecision } = this.props;

        if (hasAutomatedDecision) {
            return 'Die Anfrage hat keine Auswirkungen auf Ihren SCHUFA-Score';
        }

        return 'Fast geschafft!';
    };

    getFinishedButtonText = () => {
        const { hasAutomatedDecision } = this.props;

        if (hasAutomatedDecision) {
            return 'Erhalten Sie eine sofortige Entscheidung';
        }

        return 'Absenden';
    };

    render() {
        const {
            fields: { people },
            formValues,
            handleSubmit,
            application,
            currency
        } = this.props;

        const applicant = _.find(people, (person) => {
            return _.includes(person.roles.value, 'applicant');
        });

        const companyType = buildApplicationState(
            application.payload,
            formValues
        ).company.type;
        const COMPANY_TYPES_REQUIRE_DIRECTOR_NOTICE = [
            'gbr',
            'ohg',
            'kg',
            'ek',
            'partnershaft',
            'aktiengesellschaft',
            'kgaa',
            'gmbh',
            'ug',
            'eg',
            'gmbh_u_co_kg',
            'ltd'
        ];
        return (
            <form
                noValidate
                autoComplete="off"
                className="form-horizontal"
                onSubmit={handleSubmit(this.handleOnSubmit)}>
                <div className="form-wrapper">
                    {_.includes(
                        COMPANY_TYPES_REQUIRE_DIRECTOR_NOTICE,
                        companyType
                    ) ? (
                        <CompanyDirectorNotice />
                    ) : null}
                    {applicant ? (
                        <Person
                            person={applicant}
                            companyType={companyType}
                            currency={currency}
                        />
                    ) : null}
                </div>
                <FinishStepButton
                    type="submit"
                    onClick={() => scrollToErrors(this.props.errors)}
                    explainerText={this.getExplainerText()}>
                    {this.getFinishedButtonText()}
                </FinishStepButton>
                <TrustPilotBadge />
            </form>
        );
    }
}

const reduxFormConfig = {
    form: 'sign_up',
    destroyOnUnmount: false,
    getFormState: (state, reduxMountPoint) => state[reduxMountPoint],
    alwaysAsyncValidate: true
};

const mapStateToProps = (state) => ({
    currency: localeCurrency[getCountryCode(state)],
    ui: state.ui,
    fetched: state.fetched,
    formValues: getValues(state.form.sign_up),
    hasAutomatedDecision: hasAutomatedDecision(state),
    application: state.fetched.application,
    initialValues: {
        people: _.chain(state.fetched.application.payload.people)
            .filter((person) => _.includes(person.roles, 'applicant'))
            .map((person) => {
                return {
                    ...person,
                    roles: _.uniq([...person.roles, 'director'])
                };
            })
            .map((person) =>
                _.merge({}, _.first(applicationSchemaDE.people), person)
            )
            .value()
    }
});

const mapDispatchToProps = (dispatch) => ({
    saveCustomerState: (state) => dispatch(saveCustomerState(state)),
    getLoanDecision: () => dispatch(getLoanDecisionAction()),
    showCreditCheckOverlay: () =>
        dispatch({ type: OPEN_MODAL, modalId: 'creditCheckConsent' })
});

export default reduxForm(
    reduxFormConfig,
    mapStateToProps,
    mapDispatchToProps
)(PersonalInformationFields);
